import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static values = {
    inviteUuid: String,
    bookId: Number,
  }

  declare inviteUuidValue: string
  declare bookIdValue: number

  private selectedOptions: Map<number, number> = new Map()
  private isSaving = false
  private saveQueued = false
  private queuedSilent = true

  connect() {
    this.loadExistingSelections()
    document.addEventListener("participant:savePieVotingSelections", this.savePieSelectionsHandler)
  }

  disconnect() {
    document.removeEventListener(
      "participant:savePieVotingSelections",
      this.savePieSelectionsHandler,
    )
  }

  private savePieSelectionsHandler = (event: Event) => {
    const customEvent = event as CustomEvent<{ silent?: boolean }>
    this.savePieSelections(customEvent.detail?.silent ?? true)
  }

  selectOption(event: Event) {
    const target = event.currentTarget as HTMLElement
    const questionId = parseInt(target.dataset.questionId || "0", 10)
    const optionId = parseInt(target.dataset.optionId || "0", 10)
    if (!questionId || !optionId) return

    // Use div[data-question-id] so we get the question block, not the button (buttons also have data-question-id)
    const questionBlock = target.closest("div[data-question-id]") as HTMLElement | null
    if (!questionBlock) return

    // One option per question: store new selection (clicking another option changes selection)
    this.selectedOptions.set(questionId, optionId)

    questionBlock.querySelectorAll("button[data-option-id]").forEach((btn) => {
      const el = btn as HTMLElement
      const optId = parseInt(el.dataset.optionId || "0", 10)
      el.style.backgroundColor = optId === optionId ? "#EDF4F2" : "#EEECF6"
    })

    this.savePieSelections(true)
  }

  confirmSelection() {
    if (this.selectedOptions.size === 0) {
      this.showFlash("Please select at least one option.", "danger")
      return
    }
    this.savePieSelections(false)
  }

  private getVisibleQuestionIds(): number[] {
    const blocks = this.element.querySelectorAll("div[data-question-id]")
    const ids = new Set<number>()
    for (const el of blocks) {
      const id = parseInt((el as HTMLElement).dataset.questionId || "0", 10)
      if (id > 0) ids.add(id)
    }
    return Array.from(ids)
  }

  private async savePieSelections(silent: boolean) {
    if (this.isSaving) {
      this.saveQueued = true
      this.queuedSilent = this.queuedSilent && silent
      return
    }
    if (this.selectedOptions.size === 0) {
      if (!silent) this.showFlash("Please select at least one option.", "danger")
      return
    }

    this.isSaving = true
    const formData = new FormData()
    formData.append("invite_uuid", this.inviteUuidValue)
    formData.append("book_id", this.bookIdValue.toString())

    this.getVisibleQuestionIds().forEach((questionId) => {
      formData.append("visible_question_ids[]", questionId.toString())
    })
    this.selectedOptions.forEach((optionId, questionId) => {
      formData.append(`responses[${questionId}]`, optionId.toString())
    })

    try {
      const response = await post("/participant/save_pie_voting_response", {
        body: formData,
        headers: { Accept: "application/json" },
      })

      const result = await response.json
      if (!silent) {
        if (result.status === "ok" || result.status === "partial") {
          this.showFlash(result.message || "Your selections have been saved!", "success")
        } else {
          this.showFlash(result.error || "There was an error saving. Please try again.", "danger")
        }
      }
    } catch {
      if (!silent) {
        this.showFlash("There was an error saving your selections. Please try again.", "danger")
      }
    } finally {
      this.isSaving = false
      if (this.saveQueued) {
        this.saveQueued = false
        const queuedSilent = this.queuedSilent
        this.queuedSilent = true
        this.savePieSelections(queuedSilent)
      }
    }
  }

  private loadExistingSelections() {
    this.element.querySelectorAll("button[data-question-id][data-option-id]").forEach((btn) => {
      const el = btn as HTMLElement
      const questionId = parseInt(el.dataset.questionId || "0", 10)
      const optionId = parseInt(el.dataset.optionId || "0", 10)
      if (!questionId || !optionId) return
      const bg = window.getComputedStyle(el).backgroundColor
      const isSelected =
        bg === "rgb(237, 244, 242)" ||
        (bg.includes("237") && bg.includes("244") && bg.includes("242")) ||
        el.style?.backgroundColor?.includes("EDF4F2")
      if (isSelected) {
        this.selectedOptions.set(questionId, optionId)
      }
    })
  }

  private showFlash(message: string, variant: "success" | "danger" = "success") {
    const main = document.querySelector("main#main")
    if (!main) return

    const existingAlerts = main.querySelectorAll(".alert[data-turbo-temporary]")
    existingAlerts.forEach((alert) => {
      alert.remove()
    })

    const alert = document.createElement("div")
    alert.className = `mb-org3 alert alert-${variant} alert-dismissible fade show zIndex1`
    alert.setAttribute("role", "alert")
    alert.setAttribute("data-turbo-temporary", "true")
    alert.innerHTML = `
      ${this.escapeHtml(message)}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `
    main.insertBefore(alert, main.firstChild)

    setTimeout(() => {
      if (alert.parentNode) {
        alert.classList.remove("show")
        setTimeout(() => alert.remove(), 150)
      }
    }, 3000)
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
